import React, { useState, useContext } from 'react';
import { API, AuthContext } from '../App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Save, CheckCircle } from 'lucide-react';
import { inspectionSections } from '../data/inspectionData';

function InspectionForm() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    plate_number: '',
    driver_name: '',
    inspector_name: user?.name || '',
    inspection_date: new Date().toISOString().split('T')[0],
    general_remarks: '',
  });

  const [sections, setSections] = useState(
    inspectionSections.map(section => ({
      section_name: section.name,
      items: section.items.map(item => ({
        item_number: item.number,
        description: item.description,
        condition: 'Baik',
        remarks: '',
      })),
    }))
  );

  const totalSteps = inspectionSections.length + 2; // Header + sections + Review
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleItemChange = (sectionIndex, itemIndex, field, value) => {
    const newSections = [...sections];
    newSections[sectionIndex].items[itemIndex][field] = value;
    setSections(newSections);
  };

  const handleNext = () => {
    if (currentStep === 0) {
      if (!formData.plate_number || !formData.driver_name || !formData.inspector_name) {
        toast.error('Please fill in all required fields');
        return;
      }
    }
    setCurrentStep(currentStep + 1);
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  const calculateOverallStatus = () => {
    let hasService = false;
    let hasFailed = false;

    sections.forEach(section => {
      section.items.forEach(item => {
        if (item.condition === 'Service') hasService = true;
        if (item.condition === 'Ganti') hasFailed = true;
      });
    });

    if (hasFailed) return 'Failed';
    if (hasService) return 'Service Required';
    return 'Pass';
  };

  const handleSubmit = async () => {
    try {
      const inspectionData = {
        ...formData,
        sections,
        overall_status: calculateOverallStatus(),
      };

      await axios.post(`${API}/inspections`, inspectionData);
      toast.success('Inspection saved successfully!');
      navigate('/reports');
    } catch (error) {
      toast.error('Failed to save inspection');
    }
  };

  const renderHeader = () => (
    <div className="space-y-6" data-testid="inspection-form-header">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Inspection Details</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="plate_number">Plate Number *</Label>
          <Input
            id="plate_number"
            name="plate_number"
            data-testid="plate-number-input"
            value={formData.plate_number}
            onChange={handleInputChange}
            placeholder="e.g., B 1234 XYZ"
            className="h-11"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="driver_name">Driver Name *</Label>
          <Input
            id="driver_name"
            name="driver_name"
            data-testid="driver-name-input"
            value={formData.driver_name}
            onChange={handleInputChange}
            placeholder="Enter driver name"
            className="h-11"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="inspector_name">Inspector Name *</Label>
          <Input
            id="inspector_name"
            name="inspector_name"
            data-testid="inspector-name-input"
            value={formData.inspector_name}
            onChange={handleInputChange}
            placeholder="Enter inspector name"
            className="h-11"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="inspection_date">Inspection Date *</Label>
          <Input
            id="inspection_date"
            name="inspection_date"
            data-testid="inspection-date-input"
            type="date"
            value={formData.inspection_date}
            onChange={handleInputChange}
            className="h-11"
            required
          />
        </div>
      </div>
    </div>
  );

  const renderSection = (sectionIndex) => {
    const section = inspectionSections[sectionIndex];
    return (
      <div className="space-y-6" data-testid={`inspection-section-${sectionIndex}`}>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">{section.name}</h2>
        <div className="space-y-6">
          {section.items.map((item, itemIndex) => (
            <Card key={itemIndex} className="border-l-4 border-l-blue-500 shadow-sm">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-semibold text-blue-600 mb-1">Item {item.number}</p>
                    <p className="text-sm text-gray-700 leading-relaxed">{item.description}</p>
                  </div>
                  
                  <div className="space-y-3">
                    <Label className="text-sm font-semibold">Condition *</Label>
                    <RadioGroup
                      value={sections[sectionIndex]?.items[itemIndex]?.condition || 'Baik'}
                      onValueChange={(value) => handleItemChange(sectionIndex, itemIndex, 'condition', value)}
                      className="flex gap-4"
                      data-testid={`condition-radio-${sectionIndex}-${itemIndex}`}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="Baik" id={`baik-${sectionIndex}-${itemIndex}`} />
                        <Label htmlFor={`baik-${sectionIndex}-${itemIndex}`} className="cursor-pointer text-green-700 font-medium">Baik (Good)</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="Service" id={`service-${sectionIndex}-${itemIndex}`} />
                        <Label htmlFor={`service-${sectionIndex}-${itemIndex}`} className="cursor-pointer text-yellow-700 font-medium">Service</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="Ganti" id={`ganti-${sectionIndex}-${itemIndex}`} />
                        <Label htmlFor={`ganti-${sectionIndex}-${itemIndex}`} className="cursor-pointer text-red-700 font-medium">Ganti (Replace)</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`remarks-${sectionIndex}-${itemIndex}`} className="text-sm font-semibold">Remarks</Label>
                    <Textarea
                      id={`remarks-${sectionIndex}-${itemIndex}`}
                      data-testid={`remarks-textarea-${sectionIndex}-${itemIndex}`}
                      value={sections[sectionIndex]?.items[itemIndex]?.remarks || ''}
                      onChange={(e) => handleItemChange(sectionIndex, itemIndex, 'remarks', e.target.value)}
                      placeholder="Add any additional notes..."
                      rows={2}
                      className="resize-none"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  const renderReview = () => {
    const overallStatus = calculateOverallStatus();
    return (
      <div className="space-y-6" data-testid="inspection-review">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Review & Submit</h2>
        
        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-600 font-medium">Plate Number</p>
                <p className="text-gray-900 font-semibold mt-1">{formData.plate_number}</p>
              </div>
              <div>
                <p className="text-gray-600 font-medium">Driver</p>
                <p className="text-gray-900 font-semibold mt-1">{formData.driver_name}</p>
              </div>
              <div>
                <p className="text-gray-600 font-medium">Inspector</p>
                <p className="text-gray-900 font-semibold mt-1">{formData.inspector_name}</p>
              </div>
              <div>
                <p className="text-gray-600 font-medium">Date</p>
                <p className="text-gray-900 font-semibold mt-1">{formData.inspection_date}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`border-2 ${
          overallStatus === 'Pass' ? 'border-green-200 bg-green-50' :
          overallStatus === 'Service Required' ? 'border-yellow-200 bg-yellow-50' :
          'border-red-200 bg-red-50'
        }`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Overall Status</p>
                <p className={`text-2xl font-bold mt-1 ${
                  overallStatus === 'Pass' ? 'text-green-700' :
                  overallStatus === 'Service Required' ? 'text-yellow-700' :
                  'text-red-700'
                }`} data-testid="overall-status-display">{overallStatus}</p>
              </div>
              <CheckCircle className={`w-12 h-12 ${
                overallStatus === 'Pass' ? 'text-green-600' :
                overallStatus === 'Service Required' ? 'text-yellow-600' :
                'text-red-600'
              }`} />
            </div>
          </CardContent>
        </Card>

        <div className="space-y-2">
          <Label htmlFor="general_remarks">General Remarks</Label>
          <Textarea
            id="general_remarks"
            name="general_remarks"
            data-testid="general-remarks-textarea"
            value={formData.general_remarks}
            onChange={handleInputChange}
            placeholder="Add any general observations or recommendations..."
            rows={4}
          />
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Please review all information carefully before submitting. Once submitted, this inspection will be saved to the database.
          </p>
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    if (currentStep === 0) return renderHeader();
    if (currentStep === totalSteps - 1) return renderReview();
    return renderSection(currentStep - 1);
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6 fade-in" data-testid="inspection-form-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">New Truck Inspection</h1>
          <p className="text-gray-600 mt-1">Complete all sections to submit the inspection</p>
        </div>
      </div>

      <Card className="border-0 shadow-lg">
        <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-orange-50">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg">
                Step {currentStep + 1} of {totalSteps}
              </CardTitle>
              <span className="text-sm font-semibold text-blue-600">{Math.round(progress)}% Complete</span>
            </div>
            <Progress value={progress} className="h-2" data-testid="progress-bar" />
          </div>
        </CardHeader>
        <CardContent className="p-6 md:p-8">
          {renderCurrentStep()}

          <div className="flex justify-between mt-8 pt-6 border-t">
            <Button
              onClick={handlePrevious}
              disabled={currentStep === 0}
              variant="outline"
              data-testid="previous-button"
              className="h-11 px-6"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>

            {currentStep === totalSteps - 1 ? (
              <Button
                onClick={handleSubmit}
                data-testid="submit-inspection-button"
                className="bg-green-600 hover:bg-green-700 text-white h-11 px-8 font-semibold shadow-lg"
              >
                <Save className="w-4 h-4 mr-2" />
                Submit Inspection
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                data-testid="next-button"
                className="bg-blue-600 hover:bg-blue-700 text-white h-11 px-6"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default InspectionForm;